<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ChoiceDto, LevelDto, Locales } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import { useI18n } from 'vue-i18n';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useFormsStore } from '@/stores';
import { localizableString } from '@/helpers/validation.helper';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  row: LevelDto;
  stepId: number;
  formId: number;
  languageEnabled: Locales[];
}>();

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta } = useForm({
  initialValues: props.row,
  validationSchema: yup.object<ChoiceDto>({
    name: localizableString(props.languageEnabled),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

const dialogTitle = computed(() =>
  props.mode === 'add' ? t('form.dialogs.matrix-row.create') : t('form.dialogs.matrix-row.edit'),
);

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  await wrapSavingOperation(
    async () => await formsStore.saveMatrixRow(values, props.formId, props.stepId),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.row" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="name"
          :label="t('form.matrix-row.fields.name')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
